import { useState, useRef } from "react";
import { Header } from "@/components/layout/Header";
import { Link as RouterLink } from "react-router-dom";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Sparkles, GraduationCap, Briefcase, BookOpen,
  Laptop, Stethoscope, Factory, Palette, Wrench, Clock, MapPin,
  ExternalLink, MessageSquare, CheckCircle, Trophy, Target, Languages,
  TrendingUp, Users, Shield, Mic, FileText
} from "lucide-react";
import { ProgramChecklist } from "@/components/ProgramChecklist";
import { SkillstemAttribution } from "@/components/SkillstemAttribution";
import Seo from "@/components/Seo";
import { ShareableResultCard, type SharePathKey } from "@/components/quiz/ShareableResultCard";
import { FeedbackModule } from "@/components/quiz/FeedbackModule";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Info } from "lucide-react";

// ------- Question definitions -------
type QuizOption = { value: string; label: string; icon: string };
type QuizQuestion = {
  id: string;
  question: string;
  icon: typeof GraduationCap;
  options: QuizOption[];
  optional?: boolean;
};

const educationOptions: QuizOption[] = [
  { value: "school", label: "School Student", icon: "🏫" },
  { value: "10th", label: "10th Pass", icon: "📚" },
  { value: "12th", label: "Intermediate / 12th Pass", icon: "🎓" },
  { value: "diploma-student", label: "Diploma Student", icon: "📘" },
  { value: "diploma", label: "Diploma Completed", icon: "📗" },
  { value: "iti", label: "ITI", icon: "🛠️" },
  { value: "polytechnic", label: "Polytechnic", icon: "⚙️" },
  { value: "ug", label: "Undergraduate Student", icon: "📖" },
  { value: "graduate", label: "Graduate", icon: "🏛️" },
  { value: "postgraduate", label: "Postgraduate", icon: "🎯" },
  { value: "working", label: "Working Professional", icon: "💼" },
  { value: "switcher", label: "Career Switcher", icon: "🔄" },
  { value: "homemaker", label: "Homemaker Returning to Work", icon: "🏡" },
  { value: "jobseeker", label: "Unemployed / Job Seeker", icon: "🔍" },
  { value: "other-edu", label: "Other", icon: "✨" },
];

// Map new granular education values to legacy buckets used by program tags
const educationBucket = (val?: string): string => {
  switch (val) {
    case "school":
    case "10th":
      return "10th";
    case "12th":
    case "iti":
    case "diploma-student":
    case "diploma":
    case "polytechnic":
      return "12th";
    case "ug":
    case "graduate":
    case "postgraduate":
    case "working":
    case "switcher":
    case "homemaker":
    case "jobseeker":
    case "other-edu":
      return "graduate";
    default:
      return val ?? "";
  }
};

const englishOptions: QuizOption[] = [
  { value: "very-comfortable", label: "Very Comfortable", icon: "🌟" },
  { value: "comfortable", label: "Comfortable", icon: "✅" },
  { value: "basic", label: "Basic Understanding", icon: "📖" },
  { value: "regional", label: "Prefer Regional Language Support", icon: "🗣️" },
  { value: "not-comfortable", label: "Not Comfortable", icon: "😟" },
];

const interestOptions: QuizOption[] = [
  { value: "tech", label: "Technology & IT", icon: "💻" },
  { value: "healthcare", label: "Healthcare", icon: "🏥" },
  { value: "business", label: "Business & Marketing", icon: "📊" },
  { value: "creative", label: "Creative & Design", icon: "🎨" },
  { value: "trades", label: "Skilled Trades", icon: "🔧" },
  { value: "other", label: "My interest is not listed", icon: "✨" },
];

// Adaptive follow-up questions per interest area
const interestFollowUps: Record<string, QuizQuestion> = {
  tech: {
    id: "interest_followup",
    question: "What in tech excites you most?",
    icon: Laptop,
    options: [
      { value: "coding", label: "Coding & Software", icon: "💻" },
      { value: "data", label: "Data & AI", icon: "📊" },
      { value: "it-support", label: "IT Support & Networks", icon: "🛠️" },
      { value: "web-design", label: "Web & App Design", icon: "🎨" },
    ],
  },
  healthcare: {
    id: "interest_followup",
    question: "Which healthcare area calls to you?",
    icon: Stethoscope,
    options: [
      { value: "patient-care", label: "Patient Care & Nursing", icon: "🏥" },
      { value: "lab", label: "Lab & Diagnostics", icon: "🔬" },
      { value: "pharmacy", label: "Pharmacy & Medicine", icon: "💊" },
      { value: "public-health", label: "Public Health & Community", icon: "🌐" },
    ],
  },
  business: {
    id: "interest_followup",
    question: "Which side of business interests you?",
    icon: Briefcase,
    options: [
      { value: "marketing", label: "Sales & Marketing", icon: "📣" },
      { value: "finance", label: "Finance & Accounts", icon: "💰" },
      { value: "ops", label: "Operations & Admin", icon: "📋" },
      { value: "entrepreneur", label: "Entrepreneurship", icon: "🚀" },
    ],
  },
  creative: {
    id: "interest_followup",
    question: "What kind of creative work?",
    icon: Palette,
    options: [
      { value: "design", label: "Graphic / UI Design", icon: "🎨" },
      { value: "video", label: "Video & Photography", icon: "📹" },
      { value: "writing", label: "Content Writing", icon: "✍️" },
      { value: "media", label: "Music, Fashion or Art", icon: "🎭" },
    ],
  },
  trades: {
    id: "interest_followup",
    question: "Which trade are you drawn to?",
    icon: Wrench,
    options: [
      { value: "electrical", label: "Electrical / Electronics", icon: "⚡" },
      { value: "mechanical", label: "Mechanical / Welding", icon: "🔧" },
      { value: "automotive", label: "Automotive / AC", icon: "🚗" },
      { value: "construction", label: "Construction / Plumbing", icon: "🏗️" },
    ],
  },
};

const goalQuestion: QuizQuestion = {
  id: "goal",
  question: "What do you want right now?",
  icon: Target,
  options: [
    { value: "job", label: "Get a job quickly", icon: "💼" },
    { value: "earn-learn", label: "Earn while learning", icon: "💰" },
    { value: "skills", label: "Build new skills", icon: "🛠️" },
    { value: "govt", label: "Government career", icon: "🏛️" },
  ],
};

const timeQuestion: QuizQuestion = {
  id: "time",
  question: "How much time can you invest?",
  icon: Clock,
  options: [
    { value: "short", label: "1-3 months", icon: "⚡" },
    { value: "medium", label: "3-6 months", icon: "📅" },
    { value: "long", label: "6-12 months", icon: "📆" },
    { value: "flexible", label: "Flexible / Self-paced", icon: "🕐" },
  ],
};

const educationQuestion: QuizQuestion = {
  id: "education",
  question: "What is your current education or status?",
  icon: GraduationCap,
  options: educationOptions,
};

const englishQuestion: QuizQuestion = {
  id: "english",
  question: "How comfortable are you with English?",
  icon: Languages,
  options: englishOptions,
};

const interestQuestion: QuizQuestion = {
  id: "interest",
  question: "Which area interests you most?",
  icon: Sparkles,
  options: interestOptions,
};

// Build the dynamic question flow based on current answers
function buildFlow(answers: Record<string, string>): QuizQuestion[] {
  const flow: QuizQuestion[] = [
    educationQuestion,
    goalQuestion,
    interestQuestion,
  ];
  const interest = answers.interest;
  if (interest && interest !== "other" && interestFollowUps[interest]) {
    flow.push(interestFollowUps[interest]);
  }
  flow.push(englishQuestion, timeQuestion);
  return flow;
}

// Lightweight keyword mapper for free-text "other" interests
const interestKeywordMap: Record<string, string[]> = {
  tech: ["code", "coding", "software", "computer", "it", "ai", "data", "web", "app", "programming", "developer", "tech", "cyber", "ml", "ds", "python", "java"],
  healthcare: ["health", "medical", "doctor", "nurse", "lab", "pharma", "biology", "patient", "hospital", "care", "medicine"],
  business: ["business", "marketing", "sales", "account", "finance", "commerce", "retail", "management", "mba", "bank", "logistics"],
  creative: ["design", "art", "video", "photo", "music", "content", "writing", "edit", "animation", "fashion", "ui", "ux", "graphic", "draw", "film"],
  trades: ["electric", "mechanic", "plumb", "weld", "carpenter", "ac ", "machine", "technician", "repair", "construction", "auto", "diesel", "fitter"],
};

function mapInterestToCategory(text: string): string {
  const t = text.toLowerCase();
  for (const [cat, keywords] of Object.entries(interestKeywordMap)) {
    if (keywords.some((k) => t.includes(k))) return cat;
  }
  return "skills" === "skills" ? "business" : "business"; // safe default
}

// Program database for recommendations
const programDatabase = [
  {
    id: "pmkvy",
    name: "PMKVY",
    fullName: "Pradhan Mantri Kaushal Vikas Yojana",
    provider: "Ministry of Skill Development",
    duration: "3-6 months",
    income: "No stipend",
    outcome: "Placement Assistance + Certificate",
    url: "https://www.skillindiadigital.gov.in/pmkvy-landing",
    tags: { education: ["10th", "12th"], goal: ["job", "skills"], interest: ["tech", "healthcare", "trades", "business"], time: ["short", "medium"] },
    score: 0,
    highlight: "Government funded, multiple sectors",
  },
  {
    id: "naps",
    name: "NAPS",
    fullName: "National Apprenticeship Promotion Scheme",
    provider: "Ministry of Skill Development",
    duration: "6-36 months",
    income: "₹5,000 - ₹9,000/month",
    outcome: "Industry Experience + Certificate",
    url: "https://www.apprenticeshipindia.gov.in",
    tags: { education: ["10th", "12th", "graduate"], goal: ["earn-learn", "job"], interest: ["tech", "healthcare", "trades", "business"], time: ["medium", "long"] },
    score: 0,
    highlight: "Earn while you learn",
  },
  {
    id: "skill-india",
    name: "Skill India Digital",
    fullName: "Skill India Digital Hub",
    provider: "Government of India",
    duration: "Varies",
    income: "No stipend",
    outcome: "Certification",
    url: "https://skillindiadigital.gov.in",
    tags: { education: ["10th", "12th", "graduate"], goal: ["skills", "job"], interest: ["tech", "business", "trades"], time: ["short", "medium", "flexible"] },
    score: 0,
    highlight: "Single window for all govt schemes",
  },
  {
    id: "google-dm",
    name: "Google Digital Marketing",
    fullName: "Google Fundamentals of Digital Marketing",
    provider: "Google Skillshop",
    duration: "40 hours",
    income: "No stipend",
    outcome: "Google Certificate",
    url: "https://skillshop.withgoogle.com",
    tags: { education: ["12th", "graduate"], goal: ["skills", "job"], interest: ["business", "creative", "tech"], time: ["short", "flexible"] },
    score: 0,
    highlight: "Free Google certificate on completion",
  },
  {
    id: "freecodecamp",
    name: "FreeCodeCamp",
    fullName: "FreeCodeCamp Web Development",
    provider: "FreeCodeCamp",
    duration: "300+ hours",
    income: "No stipend",
    outcome: "Portfolio + Certificate",
    url: "https://www.freecodecamp.org/learn",
    tags: { education: ["12th", "graduate"], goal: ["skills", "job"], interest: ["tech", "creative"], time: ["medium", "long", "flexible"] },
    score: 0,
    highlight: "Learn coding for free",
  },
  {
    id: "tcs-ion",
    name: "TCS iON Career Edge",
    fullName: "TCS iON Career Edge – Young Professional",
    provider: "TCS iON",
    duration: "Self-paced",
    income: "No stipend",
    outcome: "TCS Certificate",
    url: "https://www.tcsion.com/courses/career-edge-young-professional/",
    tags: { education: ["12th", "graduate"], goal: ["job", "skills"], interest: ["business", "tech"], time: ["short", "flexible"] },
    score: 0,
    highlight: "Resume & interview prep from TCS",
  },
  {
    id: "nptel",
    name: "NPTEL / SWAYAM",
    fullName: "NPTEL & SWAYAM Free Courses",
    provider: "IITs / Government of India",
    duration: "4-12 weeks",
    income: "No stipend",
    outcome: "NPTEL Certificate (exam fee applies)",
    url: "https://swayam.gov.in",
    tags: { education: ["12th", "graduate"], goal: ["skills", "govt"], interest: ["tech", "business", "healthcare"], time: ["short", "medium", "flexible"] },
    score: 0,
    highlight: "Courses from IITs, free to learn",
  },
  {
    id: "hssc",
    name: "Healthcare Training (HSSC)",
    fullName: "Healthcare Sector Skill Council Training",
    provider: "HSSC India",
    duration: "3-6 months",
    income: "No stipend",
    outcome: "Placement Assistance",
    url: "https://www.healthcare-ssc.in/",
    tags: { education: ["12th"], goal: ["job", "skills"], interest: ["healthcare"], time: ["short", "medium"] },
    score: 0,
    highlight: "Training in healthcare sector",
  },
  {
    id: "ibm-skillsbuild",
    name: "IBM SkillsBuild",
    fullName: "IBM SkillsBuild Free Platform",
    provider: "IBM",
    duration: "Self-paced",
    income: "No stipend",
    outcome: "IBM Digital Badge",
    url: "https://skillsbuild.org",
    tags: { education: ["12th", "graduate"], goal: ["skills", "job"], interest: ["tech"], time: ["short", "medium", "flexible"] },
    score: 0,
    highlight: "AI & Cloud from IBM, free",
  },
  {
    id: "canva",
    name: "Canva Design School",
    fullName: "Canva Free Design Courses",
    provider: "Canva",
    duration: "Self-paced",
    income: "No stipend",
    outcome: "Design Skills + Certificate",
    url: "https://www.canva.com/designschool/",
    tags: { education: ["10th", "12th", "graduate"], goal: ["skills", "job"], interest: ["creative", "business"], time: ["short", "flexible"] },
    score: 0,
    highlight: "Learn graphic design free",
  },
];

// Pathway recommendations based on goal
const pathways: Record<string, { name: string; steps: string[]; stepIcons: string[]; color: string }> = {
  job: {
    name: "🚀 Job Fast Track",
    steps: ["Skill Training (PMKVY/TCS)", "Certificate", "Placement Assistance", "Employment Opportunity"],
    stepIcons: ["📚", "🎖️", "🤝", "💼"],
    color: "from-primary/20 to-primary/5",
  },
  "earn-learn": {
    name: "💰 Earn While Learn",
    steps: ["Register on NAPS", "Apprenticeship (₹5k-₹9k/mo)", "Industry Experience", "Employment Opportunity"],
    stepIcons: ["📝", "💰", "🏭", "💼"],
    color: "from-secondary/20 to-secondary/5",
  },
  skills: {
    name: "🛠️ Skill Builder",
    steps: ["Choose Interest Area", "Free Online Course", "Build Portfolio", "Freelance or Job"],
    stepIcons: ["🎯", "💻", "📂", "🚀"],
    color: "from-blue-500/20 to-blue-500/5",
  },
  govt: {
    name: "🏛️ Government Career",
    steps: ["Skill India Registration", "SWAYAM/NPTEL Courses", "Certification", "Govt Job Prep"],
    stepIcons: ["📋", "🎓", "🎖️", "🏛️"],
    color: "from-purple-500/20 to-purple-500/5",
  },
};

// Expected outcomes by goal + interest
const expectedOutcomes: Record<string, Record<string, { roles: string[]; salary: string; timeline: string }>> = {
  job: {
    tech: { roles: ["Jr. Software Dev", "IT Support", "Data Entry Operator"], salary: "₹12,000–₹25,000/mo", timeline: "3–6 months" },
    healthcare: { roles: ["Lab Technician", "Nursing Assistant", "Pharmacy Assistant"], salary: "₹10,000–₹20,000/mo", timeline: "3–6 months" },
    business: { roles: ["Sales Executive", "Digital Marketer", "Office Assistant"], salary: "₹10,000–₹22,000/mo", timeline: "2–4 months" },
    creative: { roles: ["Graphic Designer", "Social Media Manager", "Content Creator"], salary: "₹12,000–₹25,000/mo", timeline: "3–5 months" },
    trades: { roles: ["Electrician", "Welder", "AC Technician"], salary: "₹12,000–₹22,000/mo", timeline: "3–6 months" },
  },
  "earn-learn": {
    tech: { roles: ["Tech Apprentice", "IT Trainee", "Web Dev Intern"], salary: "₹5,000–₹9,000/mo (stipend) → ₹18,000+/mo", timeline: "6–12 months" },
    healthcare: { roles: ["Hospital Trainee", "Medical Assistant"], salary: "₹5,000–₹8,000/mo (stipend) → ₹15,000+/mo", timeline: "6–12 months" },
    business: { roles: ["Business Apprentice", "Marketing Trainee"], salary: "₹5,000–₹9,000/mo (stipend) → ₹18,000+/mo", timeline: "6–12 months" },
    creative: { roles: ["Design Apprentice", "Media Trainee"], salary: "₹5,000–₹8,000/mo (stipend) → ₹18,000+/mo", timeline: "6–12 months" },
    trades: { roles: ["Trade Apprentice", "Technician Trainee"], salary: "₹5,000–₹9,000/mo (stipend) → ₹20,000+/mo", timeline: "6–18 months" },
  },
  skills: {
    tech: { roles: ["Freelance Developer", "Remote Tech Support", "App Developer"], salary: "₹15,000–₹40,000/mo", timeline: "4–8 months" },
    healthcare: { roles: ["Health Educator", "Lab Specialist"], salary: "₹12,000–₹25,000/mo", timeline: "4–8 months" },
    business: { roles: ["Freelance Marketer", "Virtual Assistant", "E-commerce Seller"], salary: "₹10,000–₹35,000/mo", timeline: "3–6 months" },
    creative: { roles: ["Freelance Designer", "Video Editor", "UI Designer"], salary: "₹15,000–₹45,000/mo", timeline: "3–6 months" },
    trades: { roles: ["Self-employed Technician", "Contractor"], salary: "₹15,000–₹35,000/mo", timeline: "4–8 months" },
  },
  govt: {
    tech: { roles: ["Govt IT Officer", "Technical Assistant"], salary: "₹25,000–₹45,000/mo", timeline: "8–18 months" },
    healthcare: { roles: ["Govt Hospital Staff", "Health Inspector"], salary: "₹20,000–₹40,000/mo", timeline: "8–18 months" },
    business: { roles: ["Clerk", "Revenue Officer", "Bank PO"], salary: "₹22,000–₹45,000/mo", timeline: "8–18 months" },
    creative: { roles: ["Information Officer", "Govt Media Cell"], salary: "₹20,000–₹35,000/mo", timeline: "8–18 months" },
    trades: { roles: ["Railway Technician", "PWD Staff", "ONGC Operator"], salary: "₹22,000–₹40,000/mo", timeline: "6–12 months" },
  },
};

// Personalized SkillSTEM messaging by interest category
const interestSkillstemMessages: Record<string, string> = {
  tech: "Explore project-based technology and digital skill pathways.",
  healthcare: "Explore healthcare and life sciences learning support.",
  business: "Explore entrepreneurship, management, and business skill programs.",
  creative: "Explore creative, design, and media-oriented learning pathways.",
  trades: "Explore infrastructure, skilled trades, and industry-oriented learning pathways.",
};

// English resources for conditional recommendation
const englishResources = [
  { name: "British Council – LearnEnglish", url: "https://learnenglish.britishcouncil.org/", desc: "Speaking, listening, grammar & vocabulary" },
  { name: "BBC Learning English", url: "https://www.bbc.co.uk/learningenglish", desc: "Short daily lessons and videos" },
  { name: "SWAYAM – English Communication", url: "https://onlinecourses.swayam2.ac.in/cec26_hs08/preview", desc: "Aligned for Indian students" },
];

function getRecommendations(answers: Record<string, string>) {
  const scored = programDatabase.map((program) => {
    let score = 0;
    // Normalize education to legacy buckets so scoring works with expanded options
    const normalized: Record<string, string> = {
      ...answers,
      education: educationBucket(answers.education),
    };
    Object.entries(normalized).forEach(([key, value]) => {
      if (!value) return;
      const tagKey = key as keyof typeof program.tags;
      if (program.tags[tagKey]?.includes(value)) {
        score += key === "goal" ? 3 : key === "interest" ? 2 : 1;
      }
    });
    return { ...program, score };
  });
  return scored.sort((a, b) => b.score - a.score).slice(0, 3);
}

const socialProofMessages: Record<string, string> = {
  job: "Many students explore this pathway",
  "earn-learn": "A popular choice among learners",
  skills: "Thousands of learners explore this track",
  govt: "A common pathway for govt aspirants",
};

const CareerQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [customInterestText, setCustomInterestText] = useState("");
  const [showCustomInterest, setShowCustomInterest] = useState(false);
  const resultsShownAt = useRef<number | null>(null);

  const flow = buildFlow(answers);
  const currentQuestion = flow[Math.min(step, flow.length - 1)];
  const progress = showResults ? 100 : (step / flow.length) * 100;

  const advance = (newAnswers: Record<string, string>) => {
    const nextFlow = buildFlow(newAnswers);
    if (step < nextFlow.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      resultsShownAt.current = Date.now();
      setTimeout(() => {
        setShowResults(true);
        import("@/lib/analytics").then(({ trackEvent }) =>
          trackEvent("quiz_completed", {
            interest: newAnswers.interest,
            goal: newAnswers.goal,
          }),
        );
      }, 300);
    }
  };

  const handleAnswer = (questionId: string, value: string) => {
    // Interest "other" opens the free-text input instead of advancing
    if (questionId === "interest" && value === "other") {
      setShowCustomInterest(true);
      return;
    }
    setShowCustomInterest(false);
    const newAnswers = { ...answers, [questionId]: value };
    // If user changes interest, clear stale follow-up answer
    if (questionId === "interest" && answers.interest !== value) {
      delete newAnswers.interest_followup;
    }
    setAnswers(newAnswers);
    advance(newAnswers);
  };

  const submitCustomInterest = async () => {
    const text = customInterestText.trim();
    if (text.length < 2) return;
    const mapped = mapInterestToCategory(text);
    const newAnswers: Record<string, string> = {
      ...answers,
      interest: mapped,
      interest_custom: text.slice(0, 500),
    };
    delete newAnswers.interest_followup;
    setAnswers(newAnswers);
    setShowCustomInterest(false);
    // Fire-and-forget anonymous insert; do not block the quiz on it
    try {
      await supabase.from("custom_interests").insert({
        interest_text: text.slice(0, 500),
        mapped_category: mapped,
        education_level: answers.education ?? null,
      });
    } catch (e) {
      // Silent fail — we don't want to disrupt the user's quiz
      console.warn("custom_interests insert failed", e);
    }
    advance(newAnswers);
  };

  const trackSkillstemClick = async () => {
    const timeSpent = resultsShownAt.current ? Date.now() - resultsShownAt.current : null;
    try {
      await (supabase as any).from("analytics_events").insert({
        event_type: "skillstem_cta_click",
        interest_category: answers.interest || null,
        time_before_click_ms: timeSpent,
        metadata: {
          education: answers.education,
          goal: answers.goal,
          time: answers.time,
          english: answers.english,
        },
      });
    } catch (e) {
      console.warn("Analytics tracking failed", e);
    }
  };

  const goBack = () => {
    if (step > 0) {
      setShowCustomInterest(false);
      setStep(step - 1);
    }
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setShowResults(false);
    setCustomInterestText("");
    setShowCustomInterest(false);
  };

  const recommendations = showResults ? getRecommendations(answers) : [];
  const pathway = showResults ? pathways[answers.goal] : null;
  const needsEnglish = ["not-comfortable", "basic", "regional", "low"].includes(answers.english);
  const outcome = showResults ? expectedOutcomes[answers.goal]?.[answers.interest] : null;

  const whatsAppMessage = showResults
    ? `Hi! I used the Career Quiz on we-help.in.\n\nMy Profile:\n- Education: ${answers.education}\n- Goal: ${answers.goal}\n- Interest: ${answers.interest}\n- English: ${answers.english}\n- Time: ${answers.time}\n\nTop recommendation: ${recommendations[0]?.name}${needsEnglish ? "\n- I also need English improvement support" : ""}\n\nPlease guide me with next steps.`
    : "";

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Seo
        title="Free Career Discovery Quiz | we-help.in"
        description="Take the free Career Discovery Quiz to find the right free education, skill, and employability programs for you after 10+2."
        path="/career-quiz"
      />
      <main className="flex-1 bg-gradient-to-b from-accent/30 to-background">
        <div className="container py-8 md:py-12">
          <h1 className="sr-only">Free Career Discovery Quiz</h1>
          {/* Progress */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground font-medium">
                {showResults ? "Your Results" : `Question ${step + 1} of ${flow.length}`}
              </span>
              <span className="text-sm font-semibold text-primary">{Math.round(progress)}%</span>
            </div>
            <Progress
              value={progress}
              className="h-2"
              aria-label={showResults ? "Quiz complete" : `Quiz progress: question ${step + 1} of ${flow.length}`}
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
            />
            <SkillstemAttribution variant="inline" className="mt-2 block" />
          </div>

          <AnimatePresence mode="wait">
            {!showResults ? (
              <motion.div
                key={`${step}-${currentQuestion.id}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
                className="max-w-2xl mx-auto"
              >
                <Card className="border-0 shadow-card bg-card">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      {(() => {
                        const Icon = currentQuestion.icon;
                        return <Icon className="h-8 w-8 text-primary" />;
                      })()}
                      <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                        {currentQuestion.question}
                      </h2>
                    </div>

                    <div className="grid gap-3">
                      {currentQuestion.options.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleAnswer(currentQuestion.id, option.value)}
                          className={`flex items-center gap-4 p-4 md:p-5 min-h-[56px] rounded-xl border-2 text-left transition-all duration-200 hover:border-primary hover:bg-accent/50 hover:shadow-soft ${
                            answers[currentQuestion.id] === option.value
                              ? "border-primary bg-accent shadow-soft"
                              : "border-border bg-card"
                          }`}
                        >
                          <span className="text-2xl shrink-0">{option.icon}</span>
                          <span className="text-base md:text-lg font-medium text-foreground">{option.label}</span>
                          {answers[currentQuestion.id] === option.value && (
                            <CheckCircle className="h-5 w-5 text-primary ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Free-text capture for "My interest is not listed" */}
                    {currentQuestion.id === "interest" && showCustomInterest && (
                      <div className="mt-5 p-4 rounded-xl border-2 border-primary/40 bg-accent/40">
                        <label className="block text-sm font-semibold text-foreground mb-2">
                          Tell us what you are interested in
                        </label>
                        <Textarea
                          value={customInterestText}
                          onChange={(e) => setCustomInterestText(e.target.value.slice(0, 500))}
                          placeholder="E.g. fashion designing, drone repair, agriculture, gaming..."
                          maxLength={500}
                          rows={3}
                          className="text-base"
                        />
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3">
                          <span className="text-xs text-muted-foreground">
                            {customInterestText.length}/500 · Helps us improve recommendations for everyone
                          </span>
                          <Button
                            size="sm"
                            variant="hero"
                            onClick={submitCustomInterest}
                            disabled={customInterestText.trim().length < 2}
                            className="w-full sm:w-auto"
                          >
                            Continue <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {step > 0 && (
                      <Button variant="ghost" onClick={goBack} className="mt-6">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="max-w-3xl mx-auto space-y-8"
              >
                {/* Bold Pathway Header */}
                {pathway && (
                  <Card className="border-0 shadow-card overflow-hidden">
                    <div className={`bg-gradient-to-br ${pathway.color} p-8 md:p-10`}>
                      <Badge className="bg-primary text-primary-foreground mb-4 text-xs uppercase tracking-wider">
                        Your Personalized Result
                      </Badge>
                      <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-1">
                        Your Recommended Path:
                      </h2>
                      <h3 className="text-2xl md:text-3xl font-extrabold text-primary mb-3">
                        {pathway.name}
                      </h3>

                      {/* Social proof */}
                      <div className="flex items-center gap-2 mb-5">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground font-medium">
                          {socialProofMessages[answers.goal] || "Thousands of students chose this path"}
                        </span>
                      </div>

                      {/* Why explanation */}
                      <div className="bg-card/70 backdrop-blur rounded-xl px-5 py-4 border border-border mb-6">
                        <p className="text-base text-foreground leading-relaxed">
                          <strong className="text-primary">Why this fits you →</strong>{" "}
                          {answers.goal === "job" && `You're a ${answers.education === "10th" ? "10th" : answers.education === "12th" ? "12th" : "graduate"}-pass student interested in ${answers.interest === "tech" ? "technology" : answers.interest === "healthcare" ? "healthcare" : answers.interest === "business" ? "business" : answers.interest === "creative" ? "creative fields" : "skilled trades"} who wants a job fast. This track gets you trained, certified, and placed in the shortest time — no fees, no delays.`}
                          {answers.goal === "earn-learn" && `You want to earn money while gaining ${answers.interest === "tech" ? "tech" : answers.interest === "healthcare" ? "healthcare" : answers.interest === "business" ? "business" : answers.interest === "creative" ? "creative" : "hands-on"} experience. Apprenticeships pay ₹5,000–₹9,000/month from day one — real industry work, not classroom theory.`}
                          {answers.goal === "skills" && `You're focused on building ${answers.interest === "tech" ? "technology & coding" : answers.interest === "healthcare" ? "healthcare" : answers.interest === "business" ? "business & marketing" : answers.interest === "creative" ? "creative & design" : "trade"} skills at your own pace. This path lets you learn free, build a portfolio, and transition into freelance or full-time work.`}
                          {answers.goal === "govt" && `You're aiming for a government career in ${answers.interest === "tech" ? "technical" : answers.interest === "healthcare" ? "healthcare" : answers.interest === "business" ? "administrative" : "specialized"} roles. This track combines free IIT-level certifications with structured exam preparation.`}
                        </p>
                      </div>

                      {/* Visual career pathway — 4-step max + SkillSTEM */}
                      <div className="relative">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Your Career Roadmap</h4>
                          <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">
                            📍 You are here
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                          {[
                            ...pathway.steps.slice(0, 4).map((label, idx) => ({
                              label,
                              icon: pathway.stepIcons[idx] ?? "✅",
                              isCurrent: idx === 0,
                              isNextLevel: false,
                            })),
                            { label: "SkillSTEM Academy", icon: "🌟", isCurrent: false, isNextLevel: true },
                          ].map((s, i) => (
                            <div key={i} className="relative">
                              <div className={`rounded-xl p-4 text-center border-2 transition-all min-h-[130px] flex flex-col items-center justify-center ${
                                s.isNextLevel
                                  ? "border-primary bg-primary/10 shadow-soft"
                                  : s.isCurrent
                                    ? "border-secondary bg-secondary/10 shadow-soft"
                                    : "border-border bg-card shadow-soft"
                              }`}>
                                <div className="text-2xl mb-1" aria-hidden="true">{s.icon}</div>
                                <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${
                                  s.isNextLevel ? "text-primary" : s.isCurrent ? "text-secondary-foreground" : "text-muted-foreground"
                                }`}>
                                  {s.isNextLevel ? "Next Level" : s.isCurrent ? "Start Here" : `Step ${i + 1}`}
                                </div>
                                <div className={`text-sm font-semibold leading-tight ${
                                  s.isNextLevel ? "text-primary" : "text-foreground"
                                }`}>
                                  {s.label}
                                </div>
                                {s.isNextLevel && (
                                  <div className="text-[10px] text-muted-foreground mt-1">Mentorship + Projects</div>
                                )}
                              </div>
                              {i < 4 && (
                                <ArrowRight className="h-4 w-4 text-primary absolute -right-3.5 top-1/2 -translate-y-1/2 hidden lg:block" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Shareable Result Card */}
                {answers.goal && (
                  <ShareableResultCard
                    pathKey={answers.goal as SharePathKey}
                    onRetake={restart}
                  />
                )}

                {/* Expected Outcome */}
                {outcome && (
                  <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/20">
                    <CardContent className="p-6 md:p-8">
                      <h3 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        Indicative Outcomes
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-card rounded-xl p-5 border border-border text-center">
                          <Briefcase className="h-6 w-6 text-primary mx-auto mb-2" />
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Possible Roles</p>
                          <div className="space-y-1">
                            {outcome.roles.map((role, i) => (
                              <p key={i} className="text-sm font-semibold text-foreground">{role}</p>
                            ))}
                          </div>
                        </div>
                        <div className="bg-card rounded-xl p-5 border border-border text-center">
                          <TrendingUp className="h-6 w-6 text-primary mx-auto mb-2" />
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Typical Range*</p>
                          <p className="text-lg font-bold text-primary">{outcome.salary}</p>
                        </div>
                        <div className="bg-card rounded-xl p-5 border border-border text-center">
                          <Clock className="h-6 w-6 text-primary mx-auto mb-2" />
                          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Timeline</p>
                          <p className="text-lg font-bold text-foreground">{outcome.timeline}</p>
                          <p className="text-xs text-muted-foreground mt-1">approximate duration</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4 text-center">
                        *Indicative ranges based on publicly available data. Actual outcomes depend on location, effort, and market conditions.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Urgency banner */}
                <div className="flex items-center justify-center gap-3 bg-secondary/20 border border-secondary/30 rounded-xl px-5 py-3">
                  <span className="text-lg">📋</span>
                  <p className="text-sm font-semibold text-foreground">
                    Programs may have intake cycles — <span className="text-primary">check official websites for current availability</span>
                  </p>
                </div>

                {/* English as parallel activity — conditional */}
                {needsEnglish && (
                  <Card className="border-2 border-secondary/30 bg-gradient-to-br from-secondary/10 to-accent/30">
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-start gap-4">
                        <div className="text-4xl">🗣️</div>
                        <div className="flex-1">
                          <Badge className="bg-secondary text-secondary-foreground mb-2 text-xs">
                            Learn Alongside Your Course
                          </Badge>
                          <h3 className="text-lg font-bold text-foreground mb-2">
                            Boost Your English While You Train — Unlock Higher Salaries
                          </h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            No need to wait! Improve your English <strong className="text-foreground">in parallel</strong> with your career program. 
                            Just 15–20 minutes a day can help build the communication skills that may improve your interview confidence and job prospects.
                          </p>
                          <div className="grid gap-2 mb-4">
                            {englishResources.map((res, i) => (
                              <a
                                key={i}
                                href={res.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border hover:border-primary hover:shadow-soft transition-all"
                              >
                                <BookOpen className="h-4 w-4 text-primary shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-foreground">{res.name}</p>
                                  <p className="text-xs text-muted-foreground">{res.desc}</p>
                                </div>
                                <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
                              </a>
                            ))}
                          </div>
                          <RouterLink
                            to="/#free-english"
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                          >
                            View all free English courses <ArrowRight className="h-3 w-3" />
                          </RouterLink>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recommendations */}
                <div>
                  <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-secondary" />
                    Top Programs For You
                  </h2>
                  <div className="grid gap-4">
                    {recommendations.map((program, index) => (
                      <div key={program.id} className="space-y-2">
                        <Card className={`border hover:shadow-card transition-all ${index === 0 ? "border-primary/40 ring-1 ring-primary/20" : "border-border"}`}>
                          <CardContent className="p-6">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  {index === 0 && (
                                   <Badge className="bg-secondary text-secondary-foreground text-xs">
                                       #1 Top Match
                                    </Badge>
                                  )}
                                  {index > 0 && (
                                    <Badge variant="outline" className="text-xs">
                                      #{index + 1} Match
                                    </Badge>
                                  )}
                                </div>
                                <h3 className="text-lg font-bold text-foreground">{program.name}</h3>
                                <p className="text-sm text-muted-foreground mb-2">{program.fullName}</p>
                                <p className="text-sm text-primary font-medium mb-3">{program.highlight}</p>
                                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> {program.duration}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Briefcase className="h-3 w-3" /> {program.outcome}
                                  </span>
                                  {program.income !== "No stipend" && (
                                    <span className="flex items-center gap-1 text-green-600 font-medium">
                                      💰 {program.income}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Button asChild size="sm" variant="hero">
                                  <a href={program.url} target="_blank" rel="noopener noreferrer">
                                    Visit Official Site <ExternalLink className="h-3 w-3 ml-1" />
                                  </a>
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <ProgramChecklist programId={program.id} programName={program.name} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* What Happens Next section */}
                <Card className="border-0 shadow-card">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      What Happens Next?
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { step: "1", title: "Save Your Plan", desc: "Send results to WhatsApp so you never lose them", emoji: "📲" },
                        { step: "2", title: "Apply to Program", desc: "Click 'Visit Official Site' on your best match above", emoji: "📝" },
                        { step: "3", title: "Get Guidance", desc: "We share tips on documents, enrollment & preparation", emoji: "🤝" },
                        { step: "4", title: "Start Learning", desc: "Complete training → build skills → explore career options", emoji: "🚀" },
                      ].map((item) => (
                        <div key={item.step} className="text-center p-4 rounded-xl bg-accent/30 border border-border">
                          <span className="text-3xl block mb-2">{item.emoji}</span>
                          <div className="text-xs font-bold text-primary uppercase tracking-wider mb-1">Step {item.step}</div>
                          <h4 className="font-semibold text-foreground text-sm mb-1">{item.title}</h4>
                          <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Continue Your Learning Journey — contextual SkillSTEM transition */}
                <Card className="border border-border/80 bg-muted/30 shadow-soft">
                  <CardContent className="p-6 md:p-8">
                    <div className="max-w-2xl mx-auto text-center">
                      <Badge variant="outline" className="mb-3 text-[10px] uppercase tracking-wider text-muted-foreground border-muted-foreground/20">
                        Optional Next Step
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                        Continue Your Learning Journey
                      </h3>
                      <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-2">
                        We-Help.in helps students discover free and official education and career pathways.
                      </p>
                      <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-4">
                        Some learners may also benefit from additional structured guidance, mentoring, project-based learning, workshops, career support, or advanced learning resources offered through SkillSTEM Academy LLP.
                      </p>
                      <p className="text-xs font-semibold text-secondary-foreground bg-secondary/20 rounded-full px-3 py-1 inline-block mb-5">
                        This step is completely optional
                      </p>

                      {/* Personalized message */}
                      {answers.interest && interestSkillstemMessages[answers.interest] && (
                        <p className="text-sm text-foreground font-medium bg-card border border-border rounded-xl px-4 py-3 mb-5 inline-block">
                          {interestSkillstemMessages[answers.interest]}
                        </p>
                      )}

                      {/* CTA buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-5">
                        <a
                          href="https://skillstem.in"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={trackSkillstemClick}
                          className="inline-flex items-center justify-center gap-2 bg-foreground text-background px-6 py-3 rounded-xl font-semibold hover:bg-foreground/90 transition-colors text-sm"
                        >
                          Explore SkillSTEM Academy
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <RouterLink
                          to="/programs"
                          className="inline-flex items-center justify-center gap-2 border-2 border-border bg-card text-foreground px-6 py-3 rounded-xl font-semibold hover:bg-accent transition-colors text-sm"
                        >
                          Stay on We-Help
                        </RouterLink>
                      </div>

                      {/* Trust note */}
                      <p className="text-xs text-muted-foreground mb-5">
                        We-Help.in remains committed to promoting free and official learning opportunities.
                      </p>

                      {/* Why am I seeing this? accordion */}
                      <Accordion type="single" collapsible className="text-left">
                        <AccordionItem value="why" className="border-0">
                          <AccordionTrigger className="text-xs text-muted-foreground hover:text-foreground justify-center gap-2 py-2">
                            <Info className="h-3.5 w-3.5" />
                            Why am I seeing this?
                          </AccordionTrigger>
                          <AccordionContent>
                            <p className="text-xs text-muted-foreground leading-relaxed text-center max-w-md mx-auto">
                              We-Help.in is an initiative associated with SkillSTEM Academy LLP, which supports educational guidance and skill development initiatives.
                            </p>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  </CardContent>
                </Card>

                {/* Feedback & engagement module */}
                <FeedbackModule
                  interestCategory={answers.interest}
                  educationLevel={answers.education}
                  englishLevel={answers.english}
                  selectedRoadmap={answers.goal}
                  sourcePage="/career-quiz"
                  resultsShownAt={resultsShownAt.current}
                />

                {/* WhatsApp CTA — ongoing engagement */}
                <Card className="border-0 bg-gradient-cta text-primary-foreground shadow-cta">
                  <CardContent className="p-8 text-center">
                    <h3 className="text-2xl font-bold mb-2">📲 Save Your Results on WhatsApp</h3>
                    <p className="mb-1 opacity-90 text-base">
                      Keep your recommendations handy and get helpful career tips
                    </p>
                    <p className="text-xs opacity-60 mb-4">Powered by we-help.in · An initiative of SkillSTEM Academy LLP</p>
                    <div className="flex flex-wrap justify-center gap-4 mb-5 text-sm opacity-80">
                      <span className="flex items-center gap-1">✅ Career tips & reminders</span>
                      <span className="flex items-center gap-1">✅ Document guidance</span>
                      <span className="flex items-center gap-1">✅ Program updates</span>
                      <span className="flex items-center gap-1">✅ Community support</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button asChild variant="outline" size="lg" className="bg-card text-foreground hover:bg-card/90 font-bold">
                        <a
                          href={`https://wa.me/919390292498?text=${encodeURIComponent(whatsAppMessage)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <MessageSquare className="h-5 w-5 mr-2" />
                          Save Results on WhatsApp
                        </a>
                      </Button>
                      <Button variant="outline" size="lg" className="bg-card/20 text-primary-foreground border-primary-foreground/30 hover:bg-card/30" onClick={restart}>
                        Retake Quiz
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Browse All */}
                <div className="text-center">
                  <Button asChild variant="outline" size="lg">
                    <Link to="/programs">
                      Browse All Programs <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CareerQuiz;
